import {
  ArrowRight,
  Users,
  TrendingUp,
  Award,
  Star,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import StatsSection from "@/components/StatsSection";
import heroLeadership from "@/assets/hero-leadership.jpg";

const Index = () => {

  const pillars = [
    {
      title: "EMPOWER",
      description:
        "We provide individuals with the tools, knowledge, and confidence necessary to take ownership of their professional development.",
      features: [
        "Self-awareness and emotional intelligence building",
        "Critical thinking and problem-solving development",
        "Innovation and creative thinking enhancement",
        "Advanced decision-making capabilities",
        "Continuous learning mindset cultivation",
      ],
    },
    {
      title: "ELEVATE",
      description:
        "We support organizations to raise performance standards across all levels through comprehensive training frameworks.",
      features: [
        "Competency-based training frameworks",
        "Targeted skills gap interventions",
        "Performance measurement strategies",
        "Multi-level leadership development",
        "Organizational culture transformation",
      ],
    },
    {
      title: "EXCEL",
      description:
        "We deliver to organizations measurable improvements in productivity and satisfaction by implementing industry best practices.",
      features: [
        "Implementing industry best practices",
        "Developing high-performance teams",
        "Creating sustainable systems",
        "Fostering innovation cultures",
        "Building resilient organizations",
      ],
    },
  ];

  return (
    <Layout>
      <section
        id="home"
        className="relative min-h-screen flex items-center pt-16 md:pt-6 sm:pt-6 px-4"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroLeadership}
            alt="Professional leadership training environment"
            className="w-full h-full object-cover"
          />
          {/* DARKER OVERLAY to improve contrast */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-7xl mx-auto py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <div className="space-y-8 text-white">
              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight">
                  Transform. <span className="text-blue-400">Excel.</span>{" "}
                  <span className="text-blue-400">Lead.</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed">
                  Empowering leaders and organizations across Africa through
                  world-class training, coaching, and mentorship programs.
                </p>
              </div>

              <div className="text-center space-y-4">
                <Button size="sm" asChild className="shadow-elegant">
                  <Link to="/contact">
                    Discover Your Potential
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              <div className="flex items-center space-x-2 text-sm text-white/80">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="font-medium">
                  Trusted across Kenya and Beyond
                </span>
              </div>
            </div>

            {/* Our Promise Card */}
            <div className="relative">
              <div className="bg-blue-200/40 rounded-2xl p-8 shadow-elegant">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                  <h3 className="text-xl font-semibold mb-4">Our Promise</h3>
                  <p className="text-white/90 mb-6">
                    Every program we deliver is crafted to drive measurable
                    impact, ensuring productivity gains of 25-40% and employee
                    satisfaction improvements of 35-50%.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">25-40%</div>
                      <div className="text-xs text-white/80">
                        Productivity Gain
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">35-50%</div>
                      <div className="text-xs text-white/80">
                        Satisfaction Boost
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StatsSection></StatsSection>

      {/* Three-Pillar Approach */}
      <section className="py-12 lg:py-16 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Our Three-Pillar Approach
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Transform your organization through our comprehensive methodology
              designed to empower individuals, elevate performance, and achieve
              excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-8">
            {pillars.map((pillar, index) => (
              <Card
                key={index}
                className="h-full shadow-subtle hover:shadow-elegant transition-smooth animate-fade-in hover-scale"
              >
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto hover-scale transition-smooth">
                      <span className="text-white font-bold text-xl">
                        {pillar.title[0]}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-primary">
                      {pillar.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {pillar.description}
                    </p>
                  </div>

                  <ul className="space-y-3">
                    {pillar.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-start space-x-3"
                      >
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 bg-gradient-primary text-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Empower Your Organization’s Next Chapter
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Discover how African businesses are achieving remarkable growth
              through our innovative, results-driven training.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" asChild>
                <Link to="/contact">
                  Begin Your Transformation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                variant="secondary"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-primary"
                asChild
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
